import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Cloud, Server, Container, Workflow, Database, GitBranch, ArrowRight } from 'lucide-react';

const CloudDevOps = () => {
  const services = [
    {
      icon: Cloud,
      title: 'AWS & Azure',
      description: 'Cloud architecture, migration, cost optimization. Multi-cloud strategy and implementation.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop'
    },
    {
      icon: Container,
      title: 'Kubernetes',
      description: 'Container orchestration, microservices, scalable deployments. Cloud-native architecture.',
      image: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=600&h=400&fit=crop'
    },
    {
      icon: GitBranch,
      title: 'CI/CD Pipelines',
      description: 'Automated testing, deployment pipelines, GitOps. Release faster with confidence.',
      image: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=600&h=400&fit=crop'
    },
    {
      icon: Workflow,
      title: 'MLOps & AI Infrastructure',
      description: 'ML model deployment, GPU clusters, AI workload optimization. Production AI at scale.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop'
    },
    {
      icon: Server,
      title: 'Data Center Automation',
      description: 'Infrastructure as Code, automated provisioning, configuration management.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop'
    },
    {
      icon: Database,
      title: 'Cloud Migration',
      description: 'Lift-and-shift, re-platform, re-architect. Move to cloud with zero downtime.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop'
    }
  ];

  const technologies = [
    'AWS', 'Azure', 'Kubernetes', 'Docker', 'Terraform', 
    'Jenkins', 'GitLab CI', 'Ansible', 'Prometheus', 'Grafana'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-gray-900 to-zinc-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=800&fit=crop" 
            alt="Cloud" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Cloud className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-blue-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Cloud, DevOps & Digital Infrastructure</h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8">
              We design and operate scalable, secure, AI-ready cloud and digital platforms. Modern infrastructure for modern applications.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Modernize Your Infrastructure
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Cloud & DevOps Solutions</h2>
            <div className="w-24 h-1 bg-slate-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-slate-600" />
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Technologies We Work With</h2>
            <div className="w-24 h-1 bg-slate-600 mx-auto mb-6"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <span className="font-semibold text-gray-800">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-zinc-950 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Cloud-Native Future?</h2>
          <p className="text-lg md:text-xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Build scalable, secure infrastructure for AI and modern applications.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 text-lg px-8 transition-all hover:scale-105">
              Plan Your Cloud Strategy
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CloudDevOps;